// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — every colour that carries meaning, in one place so tests/contrast.test.ts can hold it to
// WCAG 1.4.11 (≥3:1 for a non-text graphic) against every surface it can actually sit on.
//
// The governing split, and why the board survives colour blindness and a greyscale screenshot:
// SILHOUETTE ENCODES SIDE (Ochre is a disc, Indigo is a diamond), a GOLD RING encodes rank (only a
// Keeper wears one), and hue is a redundant third channel. On top of that the two seat colours are
// separated from EACH OTHER by ≥3:1 as well as from the stone — so even a screenshot with the hue
// stripped out still reads as two different sides.

export const PALETTE = {
  ground: '#0A1520',
  panel: '#14263A',
  panelHi: '#1D3550',
  gutter: '#0E1E2E',
  edge: '#2A4664',

  /** The court. One tone — the dark gutter draws the grid, so a piece only ever sits on three
   *  surfaces (plain stone, a coffer, or the last-move highlight), all pinned by the contrast test. */
  stone: '#E3D9C6',
  coffer: '#F2DFA8',
  stoneLast: '#EFE7D8',

  /** Ochre (seat 0). Deliberately the LIGHTER of the two sides. */
  p1: '#C4551A',
  /** Indigo (seat 1). Deliberately the DARKER of the two. */
  p2: '#16255E',

  /** A legal destination ring, drawn on stone. */
  mark: '#14202E',
  /** A destination that takes a piece. */
  alarm: '#B3261E',
  /** The chrome accent — buttons, the room code, the win flourish. Drawn on dark panels. */
  gold: '#F0B93C',
  /**
   * The ring every Keeper wears. A SEPARATE colour from the chrome gold, and paler, because it is
   * drawn on the PIECES rather than on a panel and has to clear 3:1 against both of them. The
   * chrome gold measured 2.51:1 on Ochre — a ring you cannot quite see on the one piece whose
   * capture ends the game.
   */
  ring: '#FFD98A',
  ink: '#101A26',

  text: '#F4F7FB',
  textMuted: '#A9BDD4',
  /** A step printed on a toll card, and the card's own origin square. */
  tollInk: '#DCE6F2',
  tollOrigin: '#8FA6C0',
} as const;

/** Every surface a meaningful mark can actually be drawn on. */
export const SURFACES = ['stone', 'coffer', 'stoneLast'] as const;

/** Everything drawn ON the stone: pieces and the legal-move markers. */
export const ON_BOARD = ['p1', 'p2', 'mark', 'alarm', 'ink'] as const;

/** Everything drawn on top of a PIECE. Different surfaces, so a different list. */
export const ON_PIECE = ['ring'] as const;

export const SEAT_COLOUR = [PALETTE.p1, PALETTE.p2];
export const SEAT_NAME = ['Ochre', 'Indigo'];
/** Silhouette, not just hue — the channel that survives any colour vision. */
export const SEAT_SHAPE = ['disc', 'diamond'];
